import type { CSSProperties } from "react";

import { MAP_DEFAULT_PATH_COLOR } from "@ryde/shared";

/** A ring needs three corners before it encloses anything. */
export const LASSO_MIN_POINTS = 3;

/** Two corners make the first edge — below that there is nothing to draw a line through. */
export const LASSO_MIN_LINE_POINTS = 2;

/** Leaflet-geoman drew this shape in Leaflet's default path colour. */
export const LASSO_LINE_COLOR = MAP_DEFAULT_PATH_COLOR;

export const LASSO_LINE_WIDTH_PX = 2;

/** Dash and gap (px) of the rubber band running from the last placed corner to the cursor. */
const LASSO_RUBBER_BAND_DASH_PX = 2;
export const LASSO_RUBBER_BAND_DASH = [
  LASSO_RUBBER_BAND_DASH_PX,
  LASSO_RUBBER_BAND_DASH_PX,
];

/** Hollow white circle with a coloured rim — geoman's vertex handle. */
export const LASSO_VERTEX_SIZE_PX = 10;
export const LASSO_VERTEX_BORDER_PX = 2;

/**
 * How close (px) the cursor has to come to the first corner before it is magnetised onto it — closing the
 * shape on the next click. This is Leaflet-geoman's snapDistance default: the stations map drew with
 * snappable false, but geoman pushes the first vertex into _otherSnapLayers regardless of that option, and
 * _syncHintMarker self-snaps to whatever is in there. So the magnet was always on, and only ever to the
 * first corner.
 */
export const LASSO_SNAP_DISTANCE_PX = 20;

/**
 * Two clicks landing within this many pixels of each other are one corner, not two. A double-click reaches
 * the map as click, click, dblclick, so without this the gesture that closes the shape also drops a second
 * corner on top of the last one.
 */
export const LASSO_DUPLICATE_POINT_EPSILON_PX = 6;

/**
 * Gap (px) below the cursor at which the hint sits — Leaflet-geoman bound the tooltip with a 10px offset
 * and direction "bottom", and Leaflet's own .leaflet-tooltip-bottom rule adds another 6px of margin.
 */
export const LASSO_HINT_OFFSET_PX = 16;

/** Leaflet-geoman opened the hint tooltip at this opacity. */
export const LASSO_HINT_OPACITY = 0.8;

/** Side (px) of the little white notch Leaflet drew on top of a bottom-facing tooltip. */
export const LASSO_HINT_NOTCH_SIZE_PX = 6;

/**
 * The corners and the hint are decoration — every click belongs to the canvas underneath, which is where
 * the gesture is interpreted. It goes on the marker wrapper rather than the styled child, since that is
 * the element that would otherwise catch the pointer. It also keeps the crosshair from reverting to an
 * arrow over a corner: geoman put its draw cursor on the whole map container, so it never flickered.
 */
export const LASSO_MARKER_PASSTHROUGH_STYLE = {
  pointerEvents: "none",
} as const satisfies CSSProperties;
